import {
    HttpErrorResponse,
    HttpInterceptorFn,
} from '@angular/common/http';

import { inject } from '@angular/core';

import {
    catchError,
    throwError,
} from 'rxjs';

import {
    ErrorDialogService,
} from '../services/error-dialog.service';

interface BackendProblemDetails {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    message?: string;
    code?: string;
    instance?: string;
    traceId?: string;
    errors?: Record<
        string,
        string[]
    >;
}

const SESSION_REVOKED_CODE =
    'auth.session_revoked';

const SESSION_REVOCATION_UNAVAILABLE_CODE =
    'auth.session_revocation_unavailable';

export const errorInterceptor:
    HttpInterceptorFn =
    (
        request,
        next
    ) => {
        const errorDialogService =
            inject(
                ErrorDialogService
            );

        return next(
            request
        ).pipe(
            catchError(
                (
                    error:
                        HttpErrorResponse
                ) => {
                    /*
                     * FV-267:
                     *
                     * These two errors are already handled
                     * by authInterceptor.
                     *
                     * Do not additionally show the generic
                     * error dialog, otherwise the user gets
                     * duplicate/conflicting messages.
                     */
                    if (
                        isSessionRevocationError(
                            error
                        )
                    ) {
                        return throwError(
                            () => error
                        );
                    }

                    const isSettingsRequest =
                        request.url.includes(
                            '/settings'
                        );

                    if (
                        isSettingsRequest
                    ) {
                        return throwError(
                            () => error
                        );
                    }

                    const message =
                        getBackendErrorMessage(
                            error
                        );

                    const title =
                        getErrorTitle(
                            error
                        );

                    errorDialogService.show(
                        message,
                        title
                    );

                    return throwError(
                        () => error
                    );
                }
            )
        );
    };

function isSessionRevocationError(
    error: HttpErrorResponse
): boolean {
    const code =
        getBackendProblemDetails(
            error
        )?.code;

    return (
        (
            error.status ===
            401 &&
            code ===
            SESSION_REVOKED_CODE
        ) ||
        (
            error.status ===
            503 &&
            code ===
            SESSION_REVOCATION_UNAVAILABLE_CODE
        )
    );
}

function getBackendProblemDetails(
    error: HttpErrorResponse
): BackendProblemDetails | null {
    return isBackendProblemDetails(
        error.error
    )
        ? error.error
        : null;
}

function getBackendErrorMessage(
    error: HttpErrorResponse
): string {
    const backendError =
        error.error;

    if (
        typeof backendError ===
        'string' &&
        backendError.trim()
    ) {
        return backendError;
    }

    if (
        isBackendProblemDetails(
            backendError
        )
    ) {
        if (
            backendError.message
                ?.trim()
        ) {
            return backendError.message;
        }

        if (
            backendError.detail
                ?.trim()
        ) {
            return backendError.detail;
        }

        if (
            backendError.errors
        ) {
            const validationMessages =
                Object.values(
                    backendError.errors
                )
                    .flat()
                    .filter(
                        Boolean
                    )
                    .join(
                        '\n'
                    );

            if (
                validationMessages
            ) {
                return validationMessages;
            }
        }

        if (
            backendError.title
                ?.trim()
        ) {
            return backendError.title;
        }
    }

    if (
        error.status === 0
    ) {
        return 'Der Server ist aktuell nicht erreichbar.';
    }

    return 'Es ist ein unerwarteter Fehler aufgetreten.';
}

function isBackendProblemDetails(
    value: unknown
): value is BackendProblemDetails {
    return (
        typeof value ===
        'object' &&
        value !== null
    );
}

function getErrorTitle(
    error: HttpErrorResponse
): string {
    switch (
    error.status
    ) {
        case 400:
            return 'Ungültige Anfrage';

        case 401:
            return 'Nicht angemeldet';

        case 403:
            return 'Keine Berechtigung';

        case 404:
            return 'Nicht gefunden';

        case 409:
            return 'Konflikt';

        case 423:
            return 'Gesperrt';

        case 500:
            return 'Serverfehler';

        default:
            return 'Fehler';
    }
}